import api from '../../api'

// Libraries
import moment from 'moment'

// Utilities
import Analytics from '../../utility/analyticsUtil'
import AppBrowser from '../../utility/appBrowserUtil'
import isEmpty from '../../utility/isEmpty'
import Language from '../../utility/languageUtil'
import Logger from '../../utility/loggerUtil'
import RouterUtil from '../../utility/routerUtil'
import Session from '../../utility/sessionUtil'

import router from '../../router'
import store from '../../store'

import { phone } from '../../validators/phone'
import { CancelReasonEnum } from '../../constants'

export default {
  async submitPayment (context, data) {
    if (context.state.pending) {
      return
    }

    context.commit('paymentPending')

    try {
      let username = context.rootState.session.username

      if (phone(username.substring(2))) {
        username = username.substring(2)
      }

      let isRefi = false

      if (data && data.isRefi) {
        isRefi = data.isRefi
      }

      const customerName = context.rootState.customer.customerName

      context.commit('setPaymentData', { isRefi })

      const paymentObj = {
        email: phone(username) ? undefined : username,
        phoneNumber: phone(username) ? username : undefined,
        loanId: context.state.loan.loanId,
        customerName,
        storePhoneNumber: context.state.storePhoneNumber,
        productBrand: context.state.brand,
        storeId: context.state.loan.storeId,
        customerId: context.rootState.customer.customerId,
        paymentAmount: context.state.paymentAmount,
        paymentCardIds: context.state.paymentCardIds,
        paymentType: context.state.paymentType,
        disburseCardId: context.state.disburseCardId,
        cardHolderName: context.state.cardHolderName,
        cardNumber: context.state.cardNumber,
        isRefi,
        isPartial: context.rootGetters['payment/isPartialPayment'],
        collateralYear: context.state.loan.collateralDetail ? context.state.loan.collateralDetail.year : undefined,
        collateralMake: context.state.loan.collateralDetail ? context.state.loan.collateralDetail.make : undefined,
        collateralModel: context.state.loan.collateralDetail ? context.state.loan.collateralDetail.model : undefined,
        isPayoff: context.state.paymentAmount === context.state.loan.payoffAmount,
        disbursementAmount: context.state.disbursementAmount,
        disbursementType: context.state.disbursementType,
        lmsId: context.state.loan.lmsId,
        paymentDueDate: context.state.loan.dueDate,
        intentId: context.state.loan.intentId,
        migrateProduct: context.state.migrateProduct, // This means they chose to do a product migration refi
        cvv: context.state.cvv,
        guestPayment: context.state.guestPayment,
        autoPay: context.state.autoPay,
        riskCheck: context.state.riskCheck,
        ofrPaymentValue: context.state.ofrPaymentValue,
        username
      }

      const payment = await api.payment.postPayment(paymentObj)

      Logger.info('Sent payment info successfully')
      Logger.debug(payment)
      context.commit('setPaymentData', { paymentKey: payment.messageKey })
    } catch (e) {
      let serviceCallErrorText
      let serviceCallError
      if (e.response && e.response.data && e.response.data && e.response.data.Error && e.response.data.Error.length > 0) {
        serviceCallErrorText = typeof e.response.data.Error === 'string' ? e.response.data.Error : e.response.data.Error.join('\n\n')
        serviceCallError = { Error: serviceCallErrorText, disablePayButton: true }
      } else {
        if (e.message.indexOf('status code 502') > -1 || (e.response && e.response.status && e.response.status.length > 0 && e.response.status === 502)) {
          serviceCallErrorText = `${Language.$t('global.server_error')}: ${Language.$t('global.network_error')}`
          serviceCallError = { Error: serviceCallErrorText, disablePayButton: false }
        } else {
          serviceCallErrorText = `${Language.$t('global.server_error')}: ${e.message}.`
          serviceCallError = { Error: serviceCallErrorText, disablePayButton: false }
        }
      }
      context.commit('setPaymentResult', serviceCallError)
      context.commit('paymentComplete')
    }
  },

  /**
   * Continuously check the payment status from the API until we get either an error or status update.
   */
  async checkPaymentStatus (context) {
    setTimeout(async function () {
      if (context.state.pending) {
        try {
          const confirmation = await api.payment.getPaymentStatus(context.state.paymentKey)
          Logger.info('Got payment confirmation response')
          Logger.debug(confirmation)
          if (!isEmpty(confirmation) && isEmpty(confirmation.Error)) {
            if (context.state.guestCard && !context.state.saveGuestCard) { // If guest card was used and we don't want to save it, delete it
              const card = store.getters['cards/getCardById'](context.state.paymentCardIds)

              if (card !== undefined) {
                try {
                  await store.dispatch('cards/removePayDebitCard', card).catch(e => {
                    Logger.warn('Error removing card, trying again')
                    Logger.debug(e)

                    return store.dispatch('cards/removePayDebitCard', card)
                  })

                  context.commit('setPaymentData', { guestCard: false }) // Clearing guest card
                } catch (e) {
                  Logger.error('Error removing card')
                  Logger.debug(e) // Ignore error and try again on confirm payment
                }
              }
            }

            context.commit('setPaymentResult', confirmation)
            context.commit('paymentComplete')

            context.dispatch('setAlternatePartyLockout', {
              isLocked: true,
              loanId: confirmation.newLoanId
            })
          } else if (!isEmpty(confirmation.Error)) {
            throw confirmation
          } else {
            context.dispatch('checkPaymentStatus') // call this action again if need be
          }
        } catch (e) {
          if (e.Error && e.Error.length > 0) {
            const serviceCallErrorText = typeof e.Error === 'string' ? e.Error : e.Error.join('\n\n')
            const serviceCallError = { Error: serviceCallErrorText, disablePayButton: true }
            context.commit('setPaymentResult', serviceCallError)
            context.commit('paymentComplete')
          } else {
            context.dispatch('checkPaymentStatus') // Ignore errors from server and call this action again
          }
        }
      } else {
        context.commit('paymentComplete')
      }
    }, 1000)
  },

  updateRemoteState (context) {
    try {
      const cognitoId = context.rootState.session.user.username
      const currentRoute = router.currentRoute.path
      const state = btoa(JSON.stringify({
        state: context.state,
        route: currentRoute
      }))
      api.appState.postPaymentState(cognitoId, state)
    } catch (e) {
      Logger.error('Error updating state')
      Logger.debug(e)
    }
  },

  clearRemoteState (context) {
    try {
      const cognitoId = context.rootState.session.user.username
      const state = ''
      api.appState.postPaymentState(cognitoId, state)
    } catch (e) {
      Logger.error('Error updating state')
      Logger.debug(e)
    }
  },

  async setAlternatePartyLockout (context, data) {
    Logger.info(`Setting alternate party lockout to isLocked: ${data.isLocked}`)

    if (!context.rootGetters['payment/isRefiFlowEligible'] || context.getters.isSettlementPayment) {
      return true
    }

    const cacheValue = {
      profileId: context.rootGetters['session/getCognitoId'],
      customerId: context.rootState.customer.customerId,
      delete: data.isLocked === false
    }

    const postState = async function (loanId) {
      try {
        const result = await api.altParty.setLoanRefiAltPartyLockout(loanId, cacheValue)

        return result.alreadyLocked === undefined || result.alreadyLocked === false
      } catch (e) {
        Logger.error(`Error updating alternate party lockout state for Loan ID: ${loanId} (attempt 1)`)

        try {
          const result2 = await api.altParty.setLoanRefiAltPartyLockout(loanId, cacheValue)
          return result2.alreadyLocked === undefined || result2.alreadyLocked === false
        } catch (e2) {
          Logger.error(`Error updating alternate party lockout state for Loan ID: ${loanId} (attempt 2)`)
          throw e2
        }
      }
    }

    if (!isEmpty(data.loanId)) {
      return postState(data.loanId)
    }

    const oldLoanResult = await postState(context.rootState.payment.loan.loanId)

    if (oldLoanResult === false) {
      router.push({ name: 'AlternateBlocked', params: { loanId: context.rootState.payment.loan.loanId, isLocked: true, alternateName: oldLoanResult.lockedByName } })

      store.commit('payment/clearPaymentData') // Clear remote state on cancel
      store.dispatch('payment/updateRemoteState')
      return false
    }

    if (!isEmpty(context.rootState.payment.result) && !isEmpty(context.rootState.payment.result.newLoanId)) {
      const newLoanResult = await postState(context.rootState.payment.result.newLoanId)

      if (newLoanResult === false) {
        router.push({ name: 'AlternateBlocked', params: { loanId: context.rootState.payment.result.newLoanId, isLocked: true, alternateName: oldLoanResult.lockedByName } })

        store.commit('payment/clearPaymentData') // Clear remote state on cancel
        store.dispatch('payment/updateRemoteState')
        return false
      }
    }

    return true
  },

  /**
   * Cancels the refinance after the payment has already been made.
   * @param {Object} context - The Vuex store context object.
   * @param {Object} data - The config data.
   */
  async cancelRefinance (context, data = {}) {
    let username = context.rootGetters['session/getUsername']
    const isPhone = phone(username)

    if (!isEmpty(username) && isPhone) {
      username = username.substring(0, 3) + '-' + username.substring(3, 6) + '-' + username.substring(6)
    }

    const newLoanHeader = isEmpty(data.newLoanHeader) ? context.state.result.newLoanId.split('-')[2] : data.newLoanHeader
    const confirmationNumber = isEmpty(data.confirmationNumber) ? context.state.result.confirmationNumber : data.confirmationNumber

    const confirmationObj = {
      profileId: context.rootGetters['session/getCognitoId'],
      customerId: context.state.isAlternatesTurn ? context.getters.altPartyCustomerId : context.rootState.customer.customerId,
      phoneNumber: isPhone ? username : undefined,
      email: isPhone ? undefined : username,
      cardHolderName: context.state.cardHolderName,
      loanId: context.state.loan.loanId,
      amountPaid: context.state.paymentAmount,
      cardNumber: context.state.cardNumber,
      customerName: context.rootState.customer.customerName.split(' ')[0],
      storePhoneNumber: context.state.storePhoneNumber,
      productBrand: context.state.brand,
      collateralYear: context.state.loan.collateralDetail.year,
      collateralMake: context.state.loan.collateralDetail.make,
      collateralModel: context.state.loan.collateralDetail.model,
      confirmationNumber,
      paymentDueDate: context.state.loan.dueDate,
      loanSigned: data.loanSigned === true
    }

    await api.loan.cancelRenewal(newLoanHeader, confirmationObj).catch(e => {
      Logger.warn('Issue canceling renewal, trying again')
      return api.loan.cancelRenewal(newLoanHeader, confirmationObj)
    })
  },

  async getAncillaryProducts (context, data) {
    const loanId = isEmpty(data) || isEmpty(data.loanId) ? context.state.loan.loanId : data.loanId
    const paymentAmount = context.state.paymentAmount
    const productId = isEmpty(data) || isEmpty(data.productId) ? context.state.loan.productId : data.productId
    const storeId = isEmpty(data) || isEmpty(data.storeId) ? context.state.loan.storeId : data.storeId
    const ancillaryProductsResponse = await api.loan.getAncillaryProducts(loanId, paymentAmount, productId, storeId)
      .catch(e => {
        Logger.error('Error on api.loan.getAncillaryProducts')
        Logger.debug(e)
        return api.loan.getAncillaryProducts(loanId, paymentAmount, productId, storeId)
      })
    if (Array.isArray(ancillaryProductsResponse.products)) {
      context.commit('setPaymentData', {
        ancillaryProducts: ancillaryProductsResponse.products,
        ancillaryConfirmText: ancillaryProductsResponse.confirmText,
        ancillaryRequiredText: ancillaryProductsResponse.missingProducts,
        ancillaryShowApptLink: ancillaryProductsResponse.showAppointmentLink
      })
    }
  },

  async drawFunds (context) {
    const { lmsId, loanId, storeId } = context.state.loan
    const { disbursementAmount, disburseCardId, disbursementType, guestPayment } = context.state
    let username = context.rootState.session.username

    if (phone(username.substring(2))) {
      username = username.substring(2)
    }

    return api.loan.draw(loanId, storeId, disbursementAmount, disburseCardId, disbursementType, lmsId, username, guestPayment)
  },

  async getPostDrawStatus (context, data) {
    try {
      const drawStatus = await api.loan.getDrawStatus(data.messageKey)
      return drawStatus
    } catch (e) {
      Logger.error('Error getting Post Draw status.')
      Logger.debug(e)
      throw e
    }
  },

  async getNextBusinessDate (context, data) {
    const lmsId = data.lmsId
    const response = await api.store.getNextBusinessDate(lmsId) // returns a string so we still need to moment-ize it down below
      .catch(e => {
        Logger.warn('Error on api.store.getNextBusinessDate')
        Logger.debug(e)
        return api.store.getNextBusinessDate(lmsId)
      })

    if (!isEmpty(response) && !isEmpty(response.businessDate)) {
      const nextBusinessDate = moment(response.businessDate, 'YYYY-MM-DD')
      if (nextBusinessDate.isValid()) {
        context.commit('setNextBusinessDate', nextBusinessDate.toISOString())
        return nextBusinessDate.toISOString()
      }
    }

    return undefined // if we get here, uh-oh
  },

  async routeToEcommForRefinance (context) {
    try {
      const externalNavigation = true // Allow navigating back into the site and skip payment flow nav guards
      const loan = await context.dispatch('getNewLoanInfo') // Refreshing pay store loan info after initial min amount payment is recorded so restore logic will not fail due to amounts changing
      context.commit('setPaymentData', { loan })

      const result = await context.dispatch('setAlternatePartyLockout', {
        isLocked: false
      })

      if (result === false) {
        return
      }

      Session.setTokenCookie() // Set the session cookie for browsers

      context.commit('session/updateSessionTimeout', { skipTimeout: true }, { root: true }) // Skip timeout for mobile users until they return
      context.commit('setPaymentData', { externalNavigation })
      await Session.wait(500) // Wait for payment state to be saved to local storage

      Analytics.setScreenName('Ecommerce Application Site')
      AppBrowser.openInBrowser(context.state.ecommRefiUrl, '_self')
    } catch (e) {
      Logger.error('Error opening ecommerce application link in browser')
      Logger.debug(e)
      throw e
    }
  },

  async getNewLoanInfo (context) {
    const brand = RouterUtil.getCurrentRouteBrandEnum(router.currentRoute)
    await context.dispatch('loans/getLoans', { brand }, { root: true }).catch(e => {
      Logger.error('Error refreshing loan info')
      Logger.debug(e)
      return context.dispatch('loans/getLoans', { brand }, { root: true })
    })

    const loan = context.rootGetters['loans/getLoanById'](context.state.loan.loanId)

    if (isEmpty(loan)) {
      return
    }

    return loan
  },

  cancelRenewal (context) {
    if (context.state.paymentAmount > 0) {
      router.push({ name: 'ConfirmPayment', params: { isCancelledRefi: true, cancelReason: CancelReasonEnum.cancelRenewal } })
    } else {
      router.push({ name: 'Dashboard' })
    }
  },

  async checkDisbursementSuccess (context) {
    const loanId = context.state.result.newLoanId
    const disbursementType = context.state.disbursementType
    try {
      return await api.payment.getReferenceNumber(loanId, disbursementType)
    } catch (e) {
      Logger.error(`Error checking disbursement status for: ${loanId} type: ${disbursementType}`)
      Logger.debug(e)
      throw e
    }
  },

  async getDemographicReferences (context, data) {
    try {
      const demoRefs = await api.payment.getDemographicReferences(data.productId, data.customerId, data.loanId)

      if (!isEmpty(demoRefs) && !isEmpty(demoRefs.demographicReference)) {
        context.commit('setDemoRequirements', demoRefs.demographicReference)
      }
    } catch (e) {
      Logger.error('Error getting demographic references.')
      Logger.debug(e)
      throw e
    }
  },

  async updateMlaStatus (context, data) {
    try {
      await api.loan.updateMlaStatus(data.customerId, data.lmsId)
    } catch (e) {
      Logger.error('Error updating MLA status.')
      Logger.debug(e)
      // Intentionally not throwing the error
    }
  },

  async saveRestrictedStateAcknowledgement (context, data) {
    try {
      await api.payment.saveRestrictedStateAcknowledgement(context.state.loan.loanId, moment.utc().format(), data.didConsent, context.state.loan.lmsId, context.rootGetters['store/getStoreStateAbrev'](data.storeId), data.storeId, data.transactionType)
    } catch (e) {
      Logger.error('Error saving state acknowledgement')
      Logger.debug(e)
      throw e
    }
  },

  async isAddOnLocked (context) {
    try {
      if (context.state.isAddOnLocked) {
        // Reset so message can be cleared and reappear if needed.
        context.commit('setAddOnLock', false)
      }

      const response = await api.payment.isAddOnLocked()
      const isLocked = response.isLocked
      context.commit('setAddOnLock', isLocked)

      return isLocked
    } catch (e) {
      Logger.error('Error checking is add on lock.')
      Logger.debug(e)
      context.commit('setAddOnLock', false)
      return false
    }
  },

  async tryAddOnLock (context) {
    try {
      const response = await api.payment.tryAddOnLock(context.state.loan.loanId, context.state.loan.storeId, context.rootState.session.gpsPosition.latitude, context.rootState.session.gpsPosition.longitude)
      const isLocked = response.isLocked
      context.commit('setAddOnLock', isLocked)

      return isLocked
    } catch (e) {
      Logger.error('Error attempting check add on lock.')
      Logger.debug(e)

      if (e.status === 400) {
        context.commit('setAddOnLock', true)
        return true
      } else {
        return false
      }
    }
  }
}
